import api from 'configs/api'
import { getCurrentDoctorId } from 'ui/ProfessionalNPS/services'

export type VideoCallSentiment = 'negative' | 'neutral' | 'positive'

export interface VideoCallEmotionOption {
  id: string
  name: string
}

export interface VideoCallEmotionFeaturesResponse {
  title: string
  options: VideoCallEmotionOption[]
}

export interface AppointmentCsatEvaluationResponse {
  evaluation: {
    id: string
    appointment_id: string
  } | null
}

export interface CreateAppointmentCsatPayload {
  doctor_id: string
  doctor_name: string
  appointment_id: string
  stars: number
  comment: string
  type_video_call_id: string[]
}

const VIDEO_CALL_SPECIALTY = 'video_call'

function parseStoredDoctorName(): string {
  try {
    const raw = window.localStorage.getItem('@StarBem:user')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { name?: string }
    return (parsed?.name || '').trim()
  } catch {
    return ''
  }
}

export function getCurrentDoctorName(): string {
  return parseStoredDoctorName()
}

/** API uses 1 = pior, 2 = neutro, 3 = melhor — alinhado a stars no POST. */
export function sentimentToStars(sentiment: VideoCallSentiment): 1 | 2 | 3 {
  if (sentiment === 'negative') return 1
  if (sentiment === 'neutral') return 2
  return 3
}

/**
 * GET rating/v2/emotions/features?number=&specialty_type=video_call
 */
export async function fetchVideoCallEmotionFeatures(
  number: 1 | 2 | 3
): Promise<VideoCallEmotionFeaturesResponse> {
  const { data } = await api.get<VideoCallEmotionFeaturesResponse>(
    'rating/v2/emotions/features',
    {
      params: {
        number,
        specialty_type: VIDEO_CALL_SPECIALTY,
      },
    }
  )
  return {
    title: data?.title ?? '',
    options: Array.isArray(data?.options) ? data.options : [],
  }
}

/**
 * GET rating/ms/v1/csat/doctor/appointment/:appointment_id
 */
export async function fetchAppointmentCsatEvaluation(
  appointmentId: string
): Promise<AppointmentCsatEvaluationResponse> {
  const { data } = await api.get<AppointmentCsatEvaluationResponse>(
    `rating/ms/v1/csat/doctor/appointment/${appointmentId}`
  )
  return {
    evaluation: data?.evaluation ?? null,
  }
}

/**
 * POST rating/ms/v1/csat/doctor
 */
export async function createAppointmentCsat(
  payload: CreateAppointmentCsatPayload
): Promise<void> {
  await api.post('rating/ms/v1/csat/doctor', payload)
}

export async function submitVideoCallRating(input: {
  appointmentId: string
  sentiment: VideoCallSentiment
  typeVideoCallIds: string[]
  comment: string
}): Promise<void> {
  const doctorId = getCurrentDoctorId()
  const doctorName = getCurrentDoctorName()
  if (!doctorId) {
    throw new Error('missing_doctor')
  }

  await createAppointmentCsat({
    doctor_id: doctorId,
    doctor_name: doctorName || doctorId,
    appointment_id: input.appointmentId,
    stars: sentimentToStars(input.sentiment),
    comment: input.comment.trim(),
    type_video_call_id: input.typeVideoCallIds,
  })
}

export function getVideoCallRatingSubmittedKey(appointmentId: string): string {
  return `videoCallRatingSubmitted_${appointmentId}`
}

export function markVideoCallRatingSubmitted(appointmentId: string): void {
  try {
    sessionStorage.setItem(getVideoCallRatingSubmittedKey(appointmentId), '1')
  } catch {
    /* ignore quota / private mode */
  }
}

export function wasVideoCallRatingSubmitted(appointmentId: string): boolean {
  try {
    return (
      sessionStorage.getItem(getVideoCallRatingSubmittedKey(appointmentId)) ===
      '1'
    )
  } catch {
    return false
  }
}
